/**
 * "Switch to it" between tabs (W2-10 projects-data; DESIGN.md §4.1, §5.13).
 *
 * Browsers do not let a page bring another tab to the front. The tab that wants a chart posts a focus request on
 * a BroadcastChannel; the tab that has the chart open calls `window.focus()` (honoured by some browsers), makes its
 * title blink until it is looked at and says why in a toast. Without BroadcastChannel nothing is sent and the
 * requesting tab only explains where the chart is.
 */

import type { ProjectId } from '../../core/model/types';
import type { Unsubscribe } from '../../core/util/types';

export const LIBRARY_CHANNEL = 'phiedit-library';

interface FocusRequest {
  readonly kind: 'focus';
  readonly id: string;
}

function isFocusRequest(x: unknown): x is FocusRequest {
  if (typeof x !== 'object' || x === null) return false;
  const o = x as { readonly kind?: unknown; readonly id?: unknown };
  return o.kind === 'focus' && typeof o.id === 'string';
}

function channel(): BroadcastChannel | null {
  if (typeof BroadcastChannel !== 'function') return null;
  try {
    return new BroadcastChannel(LIBRARY_CHANNEL);
  } catch {
    return null;
  }
}

/** Asks the tab that has `id` open to come forward. False when tabs cannot talk to each other here. */
export function requestFocus(id: ProjectId): boolean {
  const ch = channel();
  if (ch === null) return false;
  try {
    ch.postMessage({ kind: 'focus', id } satisfies FocusRequest);
    return true;
  } catch {
    return false;
  } finally {
    ch.close();
  }
}

/** Calls `cb` with the project id of every focus request another tab posts. */
export function listenFocusRequests(cb: (id: ProjectId) => void): Unsubscribe {
  const ch = channel();
  if (ch === null) return () => undefined;
  const onMessage = (e: MessageEvent): void => {
    if (isFocusRequest(e.data)) cb(e.data.id as ProjectId);
  };
  ch.addEventListener('message', onMessage);
  return () => {
    ch.removeEventListener('message', onMessage);
    ch.close();
  };
}

/**
 * Alternates the document title with `alert` until the tab is visible and focused (or `maxMs` passed), then
 * restores it. Returns the stop function.
 */
export function blinkTitle(alert: string, maxMs = 20_000, periodMs = 1000): () => void {
  if (typeof document === 'undefined') return () => undefined;
  const original = document.title;
  const started = Date.now();
  let on = false;
  let stopped = false;
  const stop = (): void => {
    if (stopped) return;
    stopped = true;
    clearInterval(timer);
    document.removeEventListener('visibilitychange', onVisible);
    window.removeEventListener('focus', onVisible);
    document.title = original;
  };
  const onVisible = (): void => {
    if (document.visibilityState === 'visible' && document.hasFocus()) stop();
  };
  const timer = setInterval(() => {
    if (Date.now() - started > maxMs) {
      stop();
      return;
    }
    on = !on;
    document.title = on ? alert : original;
  }, periodMs);
  document.addEventListener('visibilitychange', onVisible);
  window.addEventListener('focus', onVisible);
  return stop;
}
